import React from "react";
import s from "./Badge.module.css";
import { clsx } from "../../utils/clsx";
import { useComponentDefaults } from "../../Provider";

const toneClassMap = {
  brand: s.toneBrand,
  neutral: s.toneNeutral,
  info: s.toneInfo,
  success: s.toneSuccess,
  positive: s.toneSuccess,
  danger: s.toneDanger,
  alert: s.toneDanger,
  warning: s.toneWarning,
};

const gradientClassMap = {
  danger: s.gradientDanger,
  alert: s.gradientDanger,
  success: s.gradientSuccess,
  positive: s.gradientSuccess,
};

const statusClassMap = {
  success: s.statusSuccess,
  processing: s.statusProcessing,
  default: s.statusDefault,
  error: s.statusError,
  warning: s.statusWarning,
};

const coerceNumber = (value) => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim() !== '') {
    const num = Number(value);
    if (!Number.isNaN(num)) return num;
  }
  return null;
};

const formatOffset = (value) => {
  if (value === undefined || value === null) return '0px';
  if (typeof value === 'number' && Number.isFinite(value)) return `${value}px`;
  return String(value);
};

const BadgeComponent = React.forwardRef((props, ref) => {
  const merged = useComponentDefaults(
    'Badge',
    props,
    {
      size: 'default',
      tone: 'brand',
      showZero: false,
      overflowCount: 99,
    },
    { demoteThemeKeys: ['align', 'color', 'shape', 'variant'] },
  );

  const {
    as: Component = 'span',
    children,
    className,
    style,
    count,
    countMax,
    overflowCount,
    showZero,
    dot = false,
    status,
    text,
    title,
    color,
    tone,
    gradient = false,
    size = 'default',
    offset,
    slotProps,
    ...rest
  } = merged;

  const hasChild = React.Children.count(children) > 0;
  const { root: rootSlot = {}, indicator: indicatorSlot = {} } = slotProps || {};

  const resolvedOverflow =
    overflowCount !== undefined
      ? overflowCount
      : countMax !== undefined
        ? countMax
        : 99;
  const numericCount = coerceNumber(count);
  const numericOverflow = coerceNumber(resolvedOverflow);

  const countString = count != null ? String(count) : '';
  const zeroLike = numericCount != null ? numericCount === 0 : count === 0 || countString === '0';
  const hasCount = count != null && countString.trim() !== '';
  const shouldDisplayIndicator = dot || (hasCount && (showZero || !zeroLike));

  const displayValue = dot
    ? undefined
    : numericCount != null && typeof numericOverflow === 'number' && numericCount > numericOverflow
      ? `${numericOverflow}+`
      : count;

  const toneKey = (tone || 'brand').toLowerCase();
  const resolvedToneClass = toneClassMap[toneKey] || toneClassMap.brand;
  const resolvedGradientClass = gradient ? gradientClassMap[toneKey] : undefined;
  const indicatorSizeClass = size === 'small' ? s.indicatorSmall : undefined;
  const indicatorDotClass = dot ? s.indicatorDot : undefined;
  const indicatorSingleValueClass =
    !dot && String(displayValue ?? '').length === 1 ? s.indicatorSingleValue : undefined;

  const resolvedOffset = Array.isArray(offset)
    ? offset
    : offset == null && hasChild
      ? [10, -10]
      : [0, 0];

  const indicatorStyle = {
    ...(indicatorSlot.style || {}),
    '--badge-offset-x': formatOffset(resolvedOffset[0]),
    '--badge-offset-y': formatOffset(resolvedOffset[1]),
    ...(color ? { '--badge-bg': color } : {}),
  };

  const rootStyle = { ...(rootSlot.style || {}), ...(style || {}) };
  const baseToneStyle = color ? { '--badge-bg': color } : undefined;

  if (hasChild) {
    return (
      <Component
        ref={ref}
        className={clsx(
          s.wrapper,
          resolvedToneClass,
          resolvedGradientClass,
          className,
          rootSlot.className,
        )}
        style={{ ...baseToneStyle, ...rootStyle }}
        {...rest}
      >
        {children}
        {shouldDisplayIndicator ? (
          <span
            className={clsx(
              s.indicator,
              indicatorSizeClass,
              indicatorDotClass,
              indicatorSingleValueClass,
              resolvedGradientClass,
              indicatorSlot.className,
            )}
            style={indicatorStyle}
            title={title}
            aria-hidden={!title && numericCount != null}
          >
            {dot ? null : displayValue}
          </span>
        ) : null}
      </Component>
    );
  }

  if (status) {
    const statusKey = status.toLowerCase();
    const statusClass = statusClassMap[statusKey] || statusClassMap.default;
    return (
      <Component
        ref={ref}
        className={clsx(
          s.statusRoot,
          statusClass,
          size === 'small' && s.statusSmall,
          className,
          rootSlot.className,
        )}
        style={rootStyle}
        title={title}
        {...rest}
      >
        <span
          className={clsx(s.statusDot, indicatorSlot.className)}
          style={{ ...(indicatorSlot.style || {}), ...(color ? { '--badge-bg': color } : {}) }}
        />
        {text ? <span className={s.statusText}>{text}</span> : null}
      </Component>
    );
  }

  if (hasCount && !shouldDisplayIndicator) return null;

  return (
    <Component
      ref={ref}
      className={clsx(
        s.pill,
        resolvedToneClass,
        resolvedGradientClass,
        size === 'small' && s.pillSmall,
        className,
        rootSlot.className,
      )}
      style={{ ...baseToneStyle, ...rootStyle }}
      title={title}
      {...rest}
    >
      {hasCount ? displayValue : children}
    </Component>
  );
});

BadgeComponent.displayName = 'DesignSystemBadge';

const Ribbon = ({ text, color, placement = 'end', children, className, style }) => (
  <span className={clsx(s.ribbonRoot, className)} style={style}>
    <span
      className={clsx(s.ribbon, placement === 'start' && s.ribbonStart)}
      style={{ background: color || 'var(--brand)' }}
    >
      {text}
    </span>
    {children}
  </span>
);

export default Object.assign(BadgeComponent, { Ribbon });
